import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import { PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { Can } from '@nucleus-commerce/admin-kit/permissions';
import { releaseSaveBar } from '@nucleus-commerce/admin-kit/save-bar';
import { PolicyKind, type Policy } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';

import { ViewOnStorefrontButton } from '@/features/stores';
import { useStore } from '@/shared/store/store-context';

import { emptyPolicyForm, fromPolicy, toPolicyCreate, toPolicyUpdate } from '../api/mappers';
import { DeletePolicyDialog } from '../components/delete-policy-dialog';
import { PolicyForm } from '../components/policy-form';
import { PolicyStatusBadge } from '../components/policy-status-badge';
import { usePolicies } from '../hooks/use-policies';
import { usePolicy } from '../hooks/use-policy';
import { useCreatePolicy, useUpdatePolicy } from '../hooks/use-policy-mutations';
import { policyPath } from '../schemas/policy-rules';

const NEW_ROUTE = '/_authed/_active/s/$storeId/settings/policies/new' as const;
const EDIT_ROUTE = '/_authed/_active/s/$storeId/settings/policies/$policyId' as const;

function PolicyCreate() {
  const { t } = useTranslation('policies');
  const store = useStore();
  const navigate = useNavigate();
  const { kind } = useSearch({ from: NEW_ROUTE });
  const create = useCreatePolicy(store.id);
  // Kinds already taken are disabled in the select; the list is small and usually cached.
  const existing = usePolicies(store.id);
  const taken = new Set(existing.data?.filter((p) => p.kind !== 'custom').map((p) => p.kind));
  const availableKinds = Object.values(PolicyKind).filter((k) => !taken.has(k));
  const initialKind = kind && !taken.has(kind) ? kind : (availableKinds[0] ?? 'custom');

  return (
    <PageContent>
      <PageHeader
        title={t('create.title')}
        backTo={{ to: '/s/$storeId/settings/policies', params: { storeId: store.id } }}
        backLabel={t('title')}
      />
      <PolicyForm
        key={existing.data ? 'ready' : 'loading'}
        storeId={store.id}
        policy={null}
        defaultValues={emptyPolicyForm(initialKind)}
        availableKinds={availableKinds}
        isSubmitting={create.isPending}
        onSubmit={async (values) => {
          const created = await create.mutateAsync(toPolicyCreate(values));
          await navigate({
            to: '/s/$storeId/settings/policies/$policyId',
            params: { storeId: store.id, policyId: created.id },
            replace: true,
          });
        }}
      />
    </PageContent>
  );
}

function DangerZone({ storeId, policy }: { storeId: string; policy: Policy }) {
  const { t } = useTranslation('policies');
  const navigate = useNavigate();
  const [deleting, setDeleting] = useState<Policy | null>(null);
  return (
    <FormSection title={t('danger.title')} className="border-destructive/30">
      <p className="text-muted-foreground text-sm">{t('danger.description')}</p>
      <Button
        type="button"
        variant="outline"
        className="text-destructive hover:text-destructive w-full"
        onClick={() => setDeleting(policy)}
      >
        <Trash2 aria-hidden />
        {t('actions.delete')}
      </Button>
      <DeletePolicyDialog
        storeId={storeId}
        policy={deleting}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
        onDeleted={() => {
          // The record is gone; a dirty form must not keep us on its page.
          releaseSaveBar();
          void navigate({ to: '/s/$storeId/settings/policies', params: { storeId } });
        }}
      />
    </FormSection>
  );
}

function PolicyEdit() {
  const { t } = useTranslation('policies');
  const format = useFormat();
  const store = useStore();
  const { policyId } = useParams({ from: EDIT_ROUTE });
  // Prefetched by the route loader, so this resolves from cache.
  const { data: policy } = usePolicy(store.id, policyId);
  const update = useUpdatePolicy(store.id);
  if (!policy) return null;

  return (
    <PageContent>
      <PageHeader
        title={policy.title}
        meta={<PolicyStatusBadge status={policy.status} />}
        description={
          <span title={format.utc(policy.updatedAt)}>
            {t(`kind.${policy.kind}`)}
            {' · '}
            <code>{policyPath(policy.handle)}</code>
            {' · '}
            {t('updatedAt', { when: format.relative(policy.updatedAt) })}
          </span>
        }
        backTo={{ to: '/s/$storeId/settings/policies', params: { storeId: store.id } }}
        backLabel={t('title')}
        actions={
          <ViewOnStorefrontButton url={policy.storefrontUrl} live={policy.status === 'published'} />
        }
      />
      <PolicyForm
        key={policy.updatedAt}
        storeId={store.id}
        policy={policy}
        defaultValues={fromPolicy(policy)}
        isSubmitting={update.isPending}
        onSubmit={(values) => update.mutateAsync({ id: policy.id, body: toPolicyUpdate(values) })}
        dangerZone={
          <Can permission="content.write">
            <DangerZone storeId={store.id} policy={policy} />
          </Can>
        }
      />
    </PageContent>
  );
}

export function PolicyCreatePage() {
  return (
    <FeatureErrorBoundary name="policies.create">
      <PolicyCreate />
    </FeatureErrorBoundary>
  );
}

export function PolicyEditPage() {
  return (
    <FeatureErrorBoundary name="policies.edit">
      <PolicyEdit />
    </FeatureErrorBoundary>
  );
}
